import { cameraParametersToCameraView } from "@/components/r3f/utils/camera-parameters";
import { captureModeState } from "@/features/saved-views/capture-saved-view";
import { toModeInitialState } from "@/features/saved-views/apply-saved-view";
import { selectModeInitialState, selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import {
  selectCompareElement,
  selectShouldSyncCamerasRotation,
  setSyncCamerasRotation,
} from "@/store/modes/walk-mode-slice";
import { includes } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import {
  isSavedViewCamera,
  type SavedViewData,
} from "@faro-lotv/sphere-viewer-contracts";
import { elementAndMode } from "./element-and-mode";
import { splitCompare } from "./split-compare";
import { TrackedState } from "./tracked-state";

/**
 * The camera pose, and in split mode the right camera together with the element
 * it shows and whether it follows the left camera's rotation, as a subset of a
 * saved view
 */
export type CameraPoseEntry = Pick<
  SavedViewData,
  "camera" | "rightCam" | "rightId" | "syncRot"
>;

/**
 * Tracks the camera pose. The camera moves continuously, so its changes update
 * the current entry instead of pushing new ones, and the entry the user leaves
 * keeps the last pose before the next action. The compare element `rightId` is
 * shared with {@link splitCompare}, where changing it pushes a new entry.
 *
 * The rotation lock of the split views is recorded with the cameras, as walking
 * to a waypoint locks the views again: without it, the right camera recorded
 * while they were unlocked would follow the left one on restore.
 *
 * Restoring is best effort and never rejects an entry: a mode change starts the
 * new mode at the pose, otherwise the cameras animate to it.
 */
export const cameraPose: TrackedState<CameraPoseEntry> = {
  inPlaceKeys: ["camera", "rightCam", "syncRot"],

  /**
   * @param state The current state
   * @param view The current 3d view
   * @returns the camera pose, empty while the mode has no cameras to capture
   */
  capture(state, view) {
    const mode = selectModeName(state);
    // Split mode captures the right camera too, registered once its scene mounted
    if (view.cameras.length < (mode === "split" ? 2 : 1)) return {};
    const { camera, rightCam, rightId, syncRot } = captureModeState(
      mode,
      state,
      view,
    );
    return {
      ...(camera ? { camera } : {}),
      ...(rightId && rightCam ? { rightId, rightCam, syncRot } : {}),
    };
  },

  /** @returns whether each field is either missing or well formed */
  isEntry({ camera, rightCam, rightId, syncRot }) {
    return (
      (camera === undefined || isSavedViewCamera(camera)) &&
      (rightCam === undefined || isSavedViewCamera(rightCam)) &&
      (rightId === undefined || typeof rightId === "string") &&
      (syncRot === undefined || includes(["0", "1"], syncRot))
    );
  },

  /** @returns true, a pose that cannot be applied is skipped */
  canRestore() {
    return true;
  },

  /**
   * The rotation lock is restored first. The right camera is then restored only
   * while its element is still shown and does not follow the left camera's
   * rotation anyway.
   *
   * @param previousMode The mode before the restore started, to tell whether it
   * changed the mode
   * @returns a thunk starting the mode at the pose if the restore changed the
   * mode, or else animating the cameras to it
   */
  restore(
    { camera, rightCam, rightId, syncRot },
    { sceneEvents },
    previousMode,
  ) {
    return (dispatch, getState) => {
      const state = getState();
      const mode = selectModeName(state);
      if (mode !== previousMode) {
        const right =
          rightId && selectIElement(rightId)(state)
            ? { rightId, rightCam, syncRot }
            : {};
        dispatch(
          changeMode({
            mode,
            // Keeps the initial state handed over by the parts restored before
            initialState: {
              ...selectModeInitialState(state),
              ...toModeInitialState({ camera, ...right }),
            },
          }),
        );
        return;
      }
      if (syncRot) dispatch(setSyncCamerasRotation(syncRot === "1"));
      if (camera) {
        sceneEvents.setCameraView.emit(cameraParametersToCameraView(camera));
      }
      if (
        rightCam &&
        rightId === selectCompareElement(state)?.id &&
        !selectShouldSyncCamerasRotation(getState())
      ) {
        sceneEvents.setRightCameraView.emit(
          cameraParametersToCameraView(rightCam),
        );
      }
    };
  },

  /** The mode, and in split mode the compare state, are restored first */
  restoreAfter: [elementAndMode, splitCompare],
};
